// CUSTOM: personalised-login-verification.js
// Standalone script to track login state on every page load
// Independent of personalised bundle system for reliability

(function() {
  'use strict';
  
  // Create a lightweight namespace if not available
  window.personalisedCore = window.personalisedCore || {};
  
  // Local storage key for login state
  const LOGIN_STATE_KEY = 'personalised_user_logged_in';
  
  // Track if observer is already setup to prevent duplicates
  let observerActive = false;
  
  /**
   * Standalone function to track login state changes
   * Does not depend on other personalised components
   * @returns {boolean} True if state change detected, false otherwise
   */
  function trackLoginStateStandalone() {
    try {
      if (!document.body) return false;

      // Get states
      const currentLoggedIn = document.body.dataset.customerLoggedIn === 'true';
      const previousLoggedIn = localStorage.getItem(LOGIN_STATE_KEY) === 'true';
      
      // Debug log
      console.debug('[Login Verification] Checking login state:', { 
        current: currentLoggedIn, 
        previous: previousLoggedIn 
      });
      
      // Update stored state
      localStorage.setItem(LOGIN_STATE_KEY, currentLoggedIn);
      
      // If logout detected (was logged in, now isn't)
      if (previousLoggedIn && !currentLoggedIn) {
        console.log('[Login Verification] Logout detected');
        
        // Dispatch event for any components listening
        dispatchLoginStateEvent(false);
        
        // Clear browser storage - basic cleanup independent of other modules
        clearAllPersonalisedStorage();
        
        return true; // State change detected
      }
      
      // If login detected (wasn't logged in, now is)
      if (!previousLoggedIn && currentLoggedIn) {
        console.log('[Login Verification] Login detected');
        sessionStorage.removeItem('personalised_mode'); // Clear stale guest mode so initMode() re-evaluates
        sessionStorage.setItem('personalised_session_fresh', 'true');
        dispatchLoginStateEvent(true);
        return true; // State change detected
      }
      
      return false; // No state change
    } catch (err) {
      console.error('[Login Verification] Error tracking login state:', err);
      return false;
    }
  }
  
  /**
   * Clear all personalised data from storage
   * Independent of the full bundle system
   */
  function clearAllPersonalisedStorage() {
    // Clear any personalised data from localStorage
    const keysToRemove = [];
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key && (
        key.startsWith('personalised_') || 
        key.includes('personalised') ||
        key.startsWith('vitable_') // Also clear quiz data with vitable prefix
      )) {
        keysToRemove.push(key);
      }
    }
    
    // Remove keys in a separate loop to avoid index issues
    keysToRemove.forEach(key => {
      localStorage.removeItem(key);
    });
    
    // Also explicitly clear key sessionStorage items to ensure mode is reset
    sessionStorage.removeItem('personalised_mode');
    sessionStorage.removeItem('personalised_bundle_state_modified');
    sessionStorage.removeItem('personalised_customer_id');
    sessionStorage.removeItem('personalised_recharge_token');       // PR-6.1: align with storageManager.clearAllPersonalisedData()
    sessionStorage.removeItem('personalised_last_sync_timestamp');  // PR-6.1: align with storageManager.clearAllPersonalisedData()
    // Note: personalised_session_fresh is intentionally SET (not removed) below
    
    // Set session freshness flag when clearing storage
    sessionStorage.setItem('personalised_session_fresh', 'true');
    
    console.debug('[Login Verification] Cleared personalised data from storage');
  }
  
  /**
   * Checks if the current URL path includes the Recharge portal path
   * and marks the session as fresh if it does
   * Independent of the full bundle system
   */
   
  /**
   * Lightweight fallback implementation of refreshSessionTokensOnly
   * Used when window.personalised.utils.refreshSessionTokensOnly is unavailable
   * Refreshes only Recharge session and bundle cache while preserving mode and login state
   * @param {string} reason - Reason for refreshing the session (for logging)
   */
  function refreshSessionTokensOnly(reason = 'fallback_portal_return') {
    // 1. Clear Recharge session from memory if present
    if (window.personalised?.sessionManager?.currentSession) {
      delete window.personalised.sessionManager.currentSession;
    }
    
    // 2. Remove Recharge token from sessionStorage
    sessionStorage.removeItem('personalised_recharge_token');
    
    // 3. Set the session freshness flag
    sessionStorage.setItem('personalised_session_fresh', 'true');
    
    // 4. Clear localStorage bundle cache items only (not mode or subscription_id)
    localStorage.removeItem('personalised_bundle_items');
    localStorage.removeItem('personalised_bundle_summary');
    localStorage.removeItem('personalised_bundle_error');
    
    // 5. Log confirmation message
    console.log(`[Personalised] Session and bundle cache refreshed locally (${reason}). Mode and subscription preserved.`);
  }
  
  /**
   * Primary implementation of portal return detection
   * Detects returns from the Recharge portal and refreshes session tokens without clearing mode
   * @returns {boolean} True if portal return was detected and handled
   */
  function checkForPortalReturn() {
    try {
      let portalReturnDetected = false;
      
      // Check for portal return URL parameter
      const portalReturnParam = new URLSearchParams(window.location.search).get('portal_return');
      if (portalReturnParam === 'true') {
        console.log('[Personalised] Portal return parameter detected — refreshing session only');
        portalReturnDetected = true;
        
        // Use the safer refresh method
        if (window.personalised?.utils?.refreshSessionTokensOnly) {
          window.personalised.utils.refreshSessionTokensOnly('portal_return');
        } else {
          // Fallback if utils function not available yet
          console.warn('[Personalised] Utils module refreshSessionTokensOnly not available, using local fallback');
          // Use our local implementation instead
          refreshSessionTokensOnly('portal_return');
        }

        // Remove the portal_return param from the URL
        const newUrl = window.location.pathname +
          window.location.search.replace(/[?&]portal_return=true/, '') +
          window.location.hash;

        window.history.replaceState({}, document.title, newUrl);
      }
      
      // Also check for the portal path detection
      if (window.location.pathname.includes('/tools/recurring/portal/')) {
        console.log('[Personalised] Portal URL path detected — refreshing session only');
        portalReturnDetected = true;
        
        // Use the safer refresh method - use same reason code for consistency
        if (window.personalised?.utils?.refreshSessionTokensOnly) {
          window.personalised.utils.refreshSessionTokensOnly('portal_return');
        } else {
          // Fallback if utils function not available yet
          console.warn('[Personalised] Utils module refreshSessionTokensOnly not available, using local fallback');
          // Use our local implementation instead
          refreshSessionTokensOnly('portal_path');
        }
      }
      
      // Also check for the edit path
      if (window.location.pathname.includes('/tools/recurring/edit/')) {
        console.log('[Personalised] Edit path detected — refreshing session only');
        portalReturnDetected = true;
        
        // Use the safer refresh method - use same reason code for consistency
        if (window.personalised?.utils?.refreshSessionTokensOnly) {
          window.personalised.utils.refreshSessionTokensOnly('portal_return');
        } else {
          // Fallback if utils function not available yet
          console.warn('[Personalised] Utils module refreshSessionTokensOnly not available, using local fallback');
          // Use our local implementation instead
          refreshSessionTokensOnly('portal_edit');
        }
      }
      
      return portalReturnDetected;
    } catch (err) {
      console.error('[Portal Detection] Error checking for portal return:', err);
      return false;
    }
  }
  
  /**
   * Dispatch standardized login state event
   * @param {boolean} loggedIn - Current login state
   */
  function dispatchLoginStateEvent(loggedIn) {
    const event = new CustomEvent('personalised:loginStateChanged', {
      detail: {
        loggedIn: loggedIn,
        timestamp: Date.now()
      }
    });
    
    window.dispatchEvent(event);
    console.debug(`[Login Verification] Dispatched login state event: ${loggedIn ? 'login' : 'logout'}`);
  }
  
  /**
   * Setup observer for login state changes
   * Does not depend on other personalised components
   */
  function setupLoginStateObserverStandalone() {
    // Prevent duplicate observers
    if (observerActive) return;
    
    const observer = new MutationObserver((mutations) => {
      for (const mutation of mutations) {
        if (mutation.type === 'attributes' && 
            mutation.attributeName === 'data-customer-logged-in') {
          trackLoginStateStandalone();
          break;
        }
      }
    });
    
    // Function to safely start observing when body is available
    function startObserving() {
      if (document.body) {
        observer.observe(document.body, { 
          attributes: true, 
          attributeFilter: ['data-customer-logged-in'] 
        });
        observerActive = true;
        console.debug('[Login Verification] Observer setup complete');
        
        // Run initial state check
        trackLoginStateStandalone();
        
        // Check for portal URL
        checkForPortalReturn();
      }
    }
    
    // Handle initialization based on document state
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', startObserving);
    } else {
      startObserving();
    }
  }
  
  /**
   * Get current login state
   * @returns {boolean} Current login state
   */
  function isUserLoggedIn() {
    // First try data attribute if body is available
    if (document.body) {
      return document.body.dataset.customerLoggedIn === 'true';
    }
    
    // Fall back to localStorage if body isn't available
    return localStorage.getItem(LOGIN_STATE_KEY) === 'true';
  }
  
  // Expose functions to the global namespace for the full bundle system to use
  window.personalisedCore.loginTracking = {
    trackLoginState: trackLoginStateStandalone,
    setupObserver: setupLoginStateObserverStandalone,
    isUserLoggedIn: isUserLoggedIn,
    clearStorage: clearAllPersonalisedStorage,
    checkForPortalReturn: checkForPortalReturn
  };
  
  // Also expose checkForPortalReturn directly on window.personalised
  // This makes it accessible to the utils module as the single source of truth
  window.personalised = window.personalised || {};
  window.personalised.checkForPortalReturn = checkForPortalReturn;
  
  // Self-initialize when loaded
  // This ensures login tracking is set up regardless of bundle system status
  setupLoginStateObserverStandalone();
})();
